/**
 * OperationsTable
 *
 * Sortable operations statistics table with drill-down row expansion
 * for provider breakdown and parameter correlations.
 */

import { useState } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import type { MetricsReport, OperationTimeStats, OperationReport } from '@/services/metricsAPI';

const STATUS_COLORS: Record<OperationTimeStats['pricing_status'], string> = {
  ok: 'bg-green-500/20 text-green-400 border-green-500/30',
  overcharging: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  undercharging: 'bg-red-500/20 text-red-400 border-red-500/30',
  low_confidence: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  insufficient_data: 'bg-white/10 text-white/40 border-white/10',
};

const URGENCY_COLORS = {
  critical: 'bg-red-500/20 text-red-400 border-red-500/30',
  high: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  medium: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  low: 'bg-white/10 text-white/40 border-white/10',
} as const;

const formatSeconds = (s: number): string => {
  if (s < 60) return `${s.toFixed(1)}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${(s % 60).toFixed(0)}s`;
};

const formatPercent = (v: number): string => `${v > 0 ? '+' : ''}${v.toFixed(1)}%`;

const formatParamName = (name: string, t: (k: string) => string): string => {
  const labels: Record<string, string> = {
    tts_engine: t('admin.metrics.ttsEngine'),
    source_language: t('admin.metrics.sourceLanguage'),
    generation_type: t('admin.metrics.generationType'),
    resolution: t('admin.metrics.resolution'),
    llm_model: 'LLM Model',
    text_chars: 'Text Length',
    duration_minutes: 'Duration',
    voice_id: 'Voice ID',
    voice_type: 'Voice Type',
    languages_count: 'Languages',
    scenes_count: 'Scenes',
    instruction_length: 'Instruction Length',
    edit_type: 'Edit Type',
    model: 'Model',
    steps: 'Steps',
    mode: 'Mode',
    performance: 'Performance',
  };
  return labels[name] ?? name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

interface OperationsTableProps {
  report: MetricsReport;
  selectedOperation: string | null;
  operationReport: OperationReport | null;
  loadingDetail: boolean;
  onRowClick: (operation: string) => void;
}

type SortField = keyof MetricsReport['operation_stats'][0];

export function OperationsTable({
  report,
  selectedOperation,
  operationReport,
  loadingDetail,
  onRowClick,
}: OperationsTableProps): JSX.Element {
  const { t } = useTranslation();
  const [sortField, setSortField] = useState<SortField>('operation');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [showParams, setShowParams] = useState<boolean>(false);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedOperations = [...report.operation_stats].sort((a, b) => {
    const aVal = a[sortField];
    const bVal = b[sortField];
    if (typeof aVal === 'string' && typeof bVal === 'string') {
      return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    }
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    }
    return 0;
  });

  const th = (field: SortField, label: string, align: 'left' | 'right' | 'center' = 'right') => (
    <th
      className={`text-${align} py-3 px-3 cursor-pointer hover:text-white/70`}
      onClick={() => handleSort(field)}
    >
      {label}
      {sortField === field && (sortDirection === 'asc' ? ' \u2191' : ' \u2193')}
    </th>
  );

  return (
    <div className="bg-black/30 backdrop-blur-xl rounded-xl border border-white/10 p-4 sm:p-6 mb-6 sm:mb-8 overflow-x-auto">
      <h2 className="text-lg sm:text-xl font-bold text-white mb-2">
        {t('admin.metrics.operation')} Statistics
      </h2>
      <p className="text-white/40 text-xs mb-4">{t('admin.metrics.clickForDetails')}</p>
      <div className="overflow-x-auto -mx-4 sm:mx-0">
        <table className="w-full text-sm text-white/80 min-w-[1100px]">
          <thead className="text-white/50 border-b border-white/10">
            <tr>
              {th('operation', t('admin.metrics.operation'), 'left')}
              {th('sample_count', t('admin.metrics.samples'))}
              {th('success_count', t('admin.metrics.success'))}
              {th('avg_processing_seconds', t('admin.metrics.avgTime'))}
              {th('min_processing_seconds', t('admin.metrics.minTime'))}
              {th('max_processing_seconds', t('admin.metrics.maxTime'))}
              {th('p50_processing_seconds', t('admin.metrics.p50'))}
              {th('p90_processing_seconds', t('admin.metrics.p90'))}
              {th('p99_processing_seconds', t('admin.metrics.p99'))}
              {th('avg_tokens_charged', t('admin.metrics.tokens'))}
              {th('avg_tokens_per_second', t('admin.metrics.tokensPerSecond'))}
              {th('pricing_status', t('admin.metrics.status'), 'center')}
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {sortedOperations.map((stat) => (
              <>
                <tr
                  key={`${stat.operation}-${stat.provider ?? 'all'}`}
                  className="hover:bg-white/5 cursor-pointer"
                  onClick={() => onRowClick(stat.operation)}
                >
                  <td className="py-3 px-3 font-medium">
                    {stat.operation}
                    {stat.provider && (
                      <span className="text-white/40 text-xs ml-2">({stat.provider})</span>
                    )}
                  </td>
                  <td className="py-3 px-3 text-right">{stat.sample_count}</td>
                  <td className="py-3 px-3 text-right">
                    <span
                      className={
                        stat.sample_count > 0 && stat.success_count / stat.sample_count >= 0.95
                          ? 'text-green-400'
                          : stat.sample_count > 0 && stat.success_count / stat.sample_count >= 0.85
                            ? 'text-yellow-400'
                            : 'text-red-400'
                      }
                    >
                      {stat.success_count}/{stat.sample_count}
                    </span>
                  </td>
                  <td className="py-3 px-3 text-right">
                    {formatSeconds(stat.avg_processing_seconds)}
                  </td>
                  <td className="py-3 px-3 text-right">
                    {formatSeconds(stat.min_processing_seconds)}
                  </td>
                  <td className="py-3 px-3 text-right">
                    {formatSeconds(stat.max_processing_seconds)}
                  </td>
                  <td className="py-3 px-3 text-right">
                    {formatSeconds(stat.p50_processing_seconds)}
                  </td>
                  <td className="py-3 px-3 text-right">
                    {formatSeconds(stat.p90_processing_seconds)}
                  </td>
                  <td className="py-3 px-3 text-right">
                    {formatSeconds(stat.p99_processing_seconds)}
                  </td>
                  <td className="py-3 px-3 text-right">
                    {Math.round(stat.avg_tokens_charged).toLocaleString()}
                  </td>
                  <td className="py-3 px-3 text-right">{stat.avg_tokens_per_second.toFixed(2)}</td>
                  <td className="py-3 px-3 text-center">
                    <span
                      className={`inline-block px-2 py-1 rounded border text-xs ${STATUS_COLORS[stat.pricing_status]}`}
                    >
                      {t(`admin.metrics.${stat.pricing_status}`)}
                    </span>
                  </td>
                </tr>

                {selectedOperation === stat.operation && (
                  <tr key={`${stat.operation}-detail`}>
                    <td colSpan={12} className="p-0">
                      <div className="bg-black/20 border-t border-b border-purple-500/20 p-4">
                        {loadingDetail ? (
                          <div className="text-white/50 text-center py-4">
                            {t('common.loading')}
                          </div>
                        ) : operationReport ? (
                          <div className="space-y-6">
                            {/* Provider Breakdown */}
                            <div>
                              <h3 className="text-sm font-semibold text-white/80 mb-3">
                                {t('admin.metrics.providerBreakdown')}
                              </h3>
                              {operationReport.by_provider.length > 0 ? (
                                <div className="overflow-x-auto">
                                  <table className="w-full text-xs text-white/70">
                                    <thead className="text-white/40 border-b border-white/10">
                                      <tr>
                                        <th className="text-left py-2 px-2">
                                          {t('admin.metrics.provider')}
                                        </th>
                                        <th className="text-right py-2 px-2">
                                          {t('admin.metrics.samples')}
                                        </th>
                                        <th className="text-right py-2 px-2">
                                          {t('admin.metrics.successRate')}
                                        </th>
                                        <th className="text-right py-2 px-2">
                                          {t('admin.metrics.avgTime')}
                                        </th>
                                        <th className="text-right py-2 px-2">
                                          {t('admin.metrics.p50')}
                                        </th>
                                        <th className="text-right py-2 px-2">
                                          {t('admin.metrics.p90')}
                                        </th>
                                        <th className="text-right py-2 px-2">
                                          {t('admin.metrics.tokens')}
                                        </th>
                                        <th className="text-right py-2 px-2">
                                          {t('admin.metrics.tokensPerSecond')}
                                        </th>
                                        <th className="text-center py-2 px-2">
                                          {t('admin.metrics.status')}
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-white/5">
                                      {operationReport.by_provider.map((provStat) => (
                                        <tr
                                          key={provStat.provider ?? 'unknown'}
                                          className={
                                            provStat.pricing_status === 'undercharging'
                                              ? 'bg-red-500/5'
                                              : provStat.pricing_status === 'overcharging'
                                                ? 'bg-yellow-500/5'
                                                : provStat.pricing_status === 'ok'
                                                  ? 'bg-green-500/5'
                                                  : ''
                                          }
                                        >
                                          <td className="py-2 px-2 font-medium">
                                            {provStat.provider ?? 'unknown'}
                                          </td>
                                          <td className="py-2 px-2 text-right">
                                            {provStat.sample_count}
                                          </td>
                                          <td className="py-2 px-2 text-right">
                                            <span
                                              className={
                                                provStat.sample_count > 0 &&
                                                provStat.success_count / provStat.sample_count >=
                                                  0.95
                                                  ? 'text-green-400'
                                                  : provStat.sample_count > 0 &&
                                                      provStat.success_count /
                                                        provStat.sample_count >=
                                                        0.85
                                                    ? 'text-yellow-400'
                                                    : 'text-red-400'
                                              }
                                            >
                                              {provStat.sample_count > 0
                                                ? (
                                                    (provStat.success_count /
                                                      provStat.sample_count) *
                                                    100
                                                  ).toFixed(1)
                                                : 0}
                                              %
                                            </span>
                                          </td>
                                          <td className="py-2 px-2 text-right">
                                            {formatSeconds(provStat.avg_processing_seconds)}
                                          </td>
                                          <td className="py-2 px-2 text-right">
                                            {formatSeconds(provStat.p50_processing_seconds)}
                                          </td>
                                          <td className="py-2 px-2 text-right">
                                            {formatSeconds(provStat.p90_processing_seconds)}
                                          </td>
                                          <td className="py-2 px-2 text-right">
                                            {Math.round(
                                              provStat.avg_tokens_charged
                                            ).toLocaleString()}
                                          </td>
                                          <td className="py-2 px-2 text-right">
                                            {provStat.avg_tokens_per_second.toFixed(2)}
                                          </td>
                                          <td className="py-2 px-2 text-center">
                                            <span
                                              className={`inline-block px-2 py-0.5 rounded border text-xs ${STATUS_COLORS[provStat.pricing_status]}`}
                                            >
                                              {t(`admin.metrics.${provStat.pricing_status}`)}
                                            </span>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              ) : (
                                <div className="text-white/40 text-xs py-2">
                                  {t('admin.metrics.noProviderData')}
                                </div>
                              )}
                            </div>

                            {/* Parameter Correlations */}
                            {operationReport.by_params.length > 0 && (
                              <div>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setShowParams(!showParams);
                                  }}
                                  className="flex items-center gap-2 text-sm font-semibold text-white/80 hover:text-white transition-colors mb-3"
                                >
                                  <span className="text-xs text-white/40">
                                    {showParams ? '\u25BC' : '\u25B6'}
                                  </span>
                                  {t('admin.metrics.parameterCorrelations')}
                                  <span className="text-xs text-white/40 font-normal">
                                    ({operationReport.by_params.length})
                                  </span>
                                </button>
                                {showParams && (
                                  <div className="overflow-x-auto">
                                    <table className="w-full text-xs text-white/70">
                                      <thead className="text-white/40 border-b border-white/10">
                                        <tr>
                                          <th className="text-left py-2 px-2">
                                            {t('admin.metrics.parameter')}
                                          </th>
                                          <th className="text-left py-2 px-2">
                                            {t('admin.metrics.value')}
                                          </th>
                                          <th className="text-right py-2 px-2">
                                            {t('admin.metrics.samples')}
                                          </th>
                                          <th className="text-right py-2 px-2">
                                            {t('admin.metrics.avgTime')}
                                          </th>
                                          <th className="text-right py-2 px-2">
                                            {t('admin.metrics.tokens')}
                                          </th>
                                          <th className="text-right py-2 px-2">
                                            {t('admin.metrics.recommended')}
                                          </th>
                                        </tr>
                                      </thead>
                                      <tbody className="divide-y divide-white/5">
                                        {operationReport.by_params.map((paramStat, idx) => (
                                          <tr
                                            key={`${paramStat.param_name}-${paramStat.param_value}-${idx}`}
                                          >
                                            <td className="py-2 px-2 font-medium">
                                              {formatParamName(paramStat.param_name, t)}
                                            </td>
                                            <td className="py-2 px-2">
                                              {String(paramStat.param_value)}
                                            </td>
                                            <td className="py-2 px-2 text-right">
                                              {paramStat.sample_count}
                                            </td>
                                            <td className="py-2 px-2 text-right">
                                              {formatSeconds(paramStat.avg_processing_seconds)}
                                            </td>
                                            <td className="py-2 px-2 text-right">
                                              {Math.round(
                                                paramStat.avg_tokens_charged
                                              ).toLocaleString()}
                                            </td>
                                            <td className="py-2 px-2 text-right">
                                              {paramStat.recommended_tokens.toLocaleString()}
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Operation-specific Recommendations */}
                            {operationReport.recommendations.length > 0 && (
                              <div>
                                <h3 className="text-sm font-semibold text-white/80 mb-3">
                                  {t('admin.metrics.recommendations')}
                                </h3>
                                <div className="space-y-2">
                                  {operationReport.recommendations.map((rec, idx) => (
                                    <div
                                      key={idx}
                                      className="flex items-center gap-3 text-xs text-white/70"
                                    >
                                      <span
                                        className={`px-2 py-1 rounded border text-xs ${URGENCY_COLORS[rec.urgency]}`}
                                      >
                                        {t(`admin.metrics.${rec.urgency}`)}
                                      </span>
                                      <span>
                                        {rec.provider && `[${rec.provider}] `}
                                        Current:{' '}
                                        {Math.round(rec.current_avg_tokens).toLocaleString()} tok
                                        &rarr; Recommended:{' '}
                                        {rec.recommended_tokens.toLocaleString()} tok (
                                        {formatPercent(rec.variance_percent)})
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                )}
              </>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
